import type { TraceDetailView } from '../types';

const sensitiveKey = /^(?:authorization|apiKey|serviceToken|accessToken|secret|password|cookie|system|systemPrompt|prompt|content|text|message|summary|description|responsePreview|error|errorMessage|raw|choices)$/iu;

const maskPlayerId = (value: string): string => {
  const suffix = value.slice(-4);
  return `${value.includes('_') ? `${value.split('_')[0]}_` : ''}•••${suffix}`;
};

function sanitize(value: unknown, key = ''): unknown {
  if (sensitiveKey.test(key)) return '[REDACTED]';
  if (key === 'playerId' && typeof value === 'string') return maskPlayerId(value);
  if (Array.isArray(value)) return value.map((item) => sanitize(item));
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([childKey, child]) => [childKey, sanitize(child, childKey)]));
  }
  return value;
}

export function createSanitizedTraceExport(detail: TraceDetailView, exportedAt = new Date().toISOString()): unknown {
  const source = {
    schemaVersion: 1,
    classification: 'internal-redacted',
    exportedAt,
    trace: {
      traceId: detail.traceId,
      runId: detail.runId,
      roomId: detail.roomId,
      ...(detail.playerId ? { playerId: detail.playerId } : {}),
      source: detail.source,
      status: detail.status,
      startedAt: detail.startedAt,
      ...(detail.completedAt ? { completedAt: detail.completedAt } : {}),
      ...(detail.durationMs != null ? { durationMs: detail.durationMs } : {}),
      headline: detail.headline,
      ...(detail.error ? { error: detail.error } : {}),
      ...(detail.tokenUsage ? { tokenUsage: detail.tokenUsage } : {}),
      ...(detail.promptVersion ? { promptVersion: detail.promptVersion } : {}),
      ...(detail.gameVersion ? { gameVersion: detail.gameVersion } : {}),
      ...(detail.tags ? { tags: detail.tags } : {}),
      timeline: detail.timeline.map(({ event }) => event),
      aiRoundtrips: detail.aiRoundtrips,
    },
  };
  return sanitize(source);
}

export const serializeSanitizedTrace = (detail: TraceDetailView): string =>
  JSON.stringify(createSanitizedTraceExport(detail), null, 2);
